/**
 * Client-side Nuxt plugin: when preview mode is active (Vuex flag set by the
 * SSR plugin from a valid HMAC token), force every internal navigation to
 * a full page reload with `?preview=<token>` appended.
 *
 * Why: the draft data was injected during SSR via the Apollo Link chain,
 * authenticated with a server-only Bearer token that never reaches the
 * browser. SPA navigation would skip SSR and fetch via the browser's Apollo
 * client (unauthenticated → published content), breaking the preview
 * experience and confusing the editor.
 *
 * Behaviour:
 *   - Hooks the router's beforeEach guard.
 *   - When preview is active and the next route is on the same origin,
 *     replace SPA nav with `window.location.assign(<path>?preview=<token>)`.
 *   - External links (`<a target="_blank">`, off-site URLs) are unaffected.
 *   - Cost: each click feels like a full reload. Acceptable for editors;
 *     a clear visual signal they're in preview.
 *
 * Token: the SSR plugin stashes the raw token in the preview Vuex state on
 * the initial render. The browser's hydrated Vuex contains it. We re-append
 * it on each nav so the new SSR run re-validates the HMAC. The token's
 * 24h TTL is enforced server-side; nothing client-side trusts it.
 */

export default function ({ app, store }) {
  if (!store || !store.getters || !store.getters['preview/isPreview']) {
    return
  }

  if (!app || !app.router) {
    return
  }

  app.router.beforeEach((to, from, next) => {
    const token = store.state.preview && store.state.preview.token
    if (!token) {
      // No token surviving hydration — let Nuxt nav happen normally; user is
      // effectively dropped out of preview. Banner will disappear on reload.
      return next()
    }

    // CRITICAL: skip the initial route resolution that fires right after
    // SSR hydration. The server already rendered the correct preview page;
    // forcing another reload here causes an infinite reload loop
    // (hydrate -> beforeEach -> reload -> hydrate -> ...).
    //
    // Vue Router signals the initial resolution by `from === START_LOCATION`,
    // which has no matched routes and a null name.
    if (!from || !from.matched || from.matched.length === 0) {
      return next()
    }

    // Same-path navigation (e.g. hash change, query-only change) — let Vue
    // Router handle it without a reload. The Apollo cache is already hydrated
    // with draft data for this page from SSR.
    if (to.path === from.path) {
      return next()
    }

    // NOTE: do NOT special-case "destination has no preview query" as an
    // exit-intent here. The exit button in <PreviewBanner> bypasses Vue
    // Router entirely by calling window.location.assign() directly, so this
    // hook never sees it. Treating "no preview query" as exit-intent would
    // SPA-navigate internal <nuxt-link> clicks (which never carry the query)
    // to a draft URL without re-stamping the token, leaving the editor in a
    // half-state where the banner stays but the Bearer header is gone.
    //
    // Real cross-page nav with preview still active — force full reload so
    // SSR re-runs, the HMAC re-validates, the Apollo Link chain re-attaches
    // the Bearer header, and the new page renders draft content end-to-end.
    const path = to.fullPath || to.path || '/'
    const sep = path.includes('?') ? '&' : '?'
    const target = path + (path.includes('preview=') ? '' : (sep + 'preview=' + encodeURIComponent(token)))

    window.location.assign(target)
    // Do NOT call next() — pending nav is replaced by full reload.
  })

  // ---------------------------------------------------------------------------
  // Document-level click guard for plain <a href="/..."> anchors.
  //
  // Why this is needed in addition to router.beforeEach:
  //   The site renders Strapi-authored markdown via `v-html="$md.render(...)"`
  //   in 20+ components (product bodies, FAQ, home tiles, etc). Any link an
  //   author writes in rich text (e.g. `[Dealers](/dealers)`) becomes a plain
  //   DOM <a> — NOT a NuxtLink. Vue Router never sees the click, so the
  //   `beforeEach` hook never fires; the browser does a native hard navigation
  //   to `/dealers`, the `?preview=` query is lost, and the editor silently
  //   drops out of preview mode.
  //
  // Strategy:
  //   - Listen in BUBBLE phase so Vue Router's per-element handler runs first.
  //   - If the click was already preventDefault()'d → it's a NuxtLink and the
  //     beforeEach above will handle it. Skip.
  //   - Otherwise it's a plain anchor: if same-origin root-relative, rewrite
  //     to include `?preview=<token>` and force a full reload via location.
  //
  // Skipped: modifier keys (new-tab intent), middle/right click, target=_blank,
  // hash-only anchors, external URLs, mailto/tel, javascript: pseudo-URLs.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return
    if (e.button !== 0) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

    const a = e.target && e.target.closest && e.target.closest('a[href]')
    if (!a) return

    const target = a.getAttribute('target')
    if (target && target !== '' && target !== '_self') return

    const href = a.getAttribute('href')
    if (!href) return
    // Only intercept same-origin root-relative paths. Protocol-relative
    // (//host/...) and absolute URLs (http://...) are treated as external.
    if (!href.startsWith('/') || href.startsWith('//')) return

    const token = store.state.preview && store.state.preview.token
    if (!token) return
    if (!store.getters['preview/isPreview']) return

    e.preventDefault()
    const sep = href.includes('?') ? '&' : '?'
    const rewritten = href + (href.includes('preview=') ? '' : sep + 'preview=' + encodeURIComponent(token))
    window.location.assign(rewritten)
  }, false)
}
